import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import CSSModules from "lib/utils/css-modules";

import {
  st,
  ProductVariantDecorator,
  FormControl,
  Form,
  FormMessage,
  Accordion,
  HintView,
  Message,
  StockAvailability,
} from "lib/index";
import { stateSelector, updateProduct, validate, save } from "../index";
import ProductVariantsListing from "../ProductVariantsListing";
import Hint from "./StepBookingSettingsHint";
import ProductVariantCouponCodesFormSection from "./ProductVariantCouponCodesFormSection";
import { NoBookingStockAvailability } from "../../../../lib/components/StockAvailability";
import { SALE_WINDOW_EDGES } from "lib/utils/saleWindow";

class ProductVariantFormSection extends React.PureComponent {
  handleProductVariantPropChange(prop, value) {
    const { product_variant, onChange } = this.props;

    onChange(
      new ProductVariantDecorator({
        ...product_variant,
        [prop]: value,
      })
    );
  }

  handleProductPropChange(prop, value) {
    const { product, updateProduct } = this.props;
    updateProduct({
      ...product,
      [prop]: value,
    });
  }

  handleProductVariantPropsChange(data) {
    const { product_variant, onChange } = this.props;

    onChange(
      new ProductVariantDecorator({
        ...product_variant,
        ...data,
      })
    );
  }

  // One edge of the default sale window. An offset and a fixed datetime are
  // mutually exclusive, so both attributes are always written together.
  handleDefaultSaleWindowChange(attribute, { offset_seconds, at }) {
    this.handleProductVariantPropsChange({
      [`default_${attribute}_offset_seconds`]: offset_seconds,
      [`default_${attribute}`]: at,
    });
  }

  // "Otwarcie sprzedaży" / "Zamknięcie sprzedaży" defaults, pre-filled into
  // the form when the operator adds new dates for this variant. Only shown
  // for products that actually book dates, next to the other
  // "new date" defaults.
  renderDefaultSaleWindowInputs() {
    const { readOnlyAccess, companyTimeZoneName, product_variant, errors, name, onBlur } = this.props;

    return SALE_WINDOW_EDGES.map(({ attribute, edge, labelKey }) => (
      <FormControl.SaleWindowRuleFormGroup
        key={attribute}
        {...FormControl.addProps(`${name}.default_${attribute}`, { errors })}
        value={{
          offset_seconds: product_variant[`default_${attribute}_offset_seconds`],
          at: product_variant[`default_${attribute}`],
        }}
        onBlur={onBlur}
        onChange={this.handleDefaultSaleWindowChange.bind(this, attribute)}
        label={I18n.t(labelKey)}
        edge={edge}
        timeZoneName={companyTimeZoneName}
        readOnly={readOnlyAccess}
      />
    ));
  }

  render() {
    const {
      readOnlyAccess,
      companyTimeZoneName,
      product_booking_type,
      product_variant,
      product_type,
      errors,
      name,
      onBlur,
      onChange,
    } = this.props;

    product_variant.nb_total_stock_limit = product_variant.nb_total_stock_limit || null;

    return (
      <div>
        {!!errors[name] && <FormMessage error={errors[name]} />}
        {product_booking_type === "no_booking" && product_type !== "accommodation" && (
          <div>
            <FormControl.UsebyDateRangeFormGroup
              label={I18n.t("papp.products.EditProductView.StepBookingSettings.nb_stock_useby_date_label")}
              value={{
                days_limit: product_variant.nb_stock_useby_days_limit,
                starts_at: product_variant.nb_stock_useby_starts_at,
                ends_at: product_variant.nb_stock_useby_ends_at,
              }}
              onBlur={onBlur}
              onChange={({
                days_limit: nb_stock_useby_days_limit,
                starts_at: nb_stock_useby_starts_at,
                ends_at: nb_stock_useby_ends_at,
              }) =>
                this.handleProductVariantPropsChange({
                  nb_stock_useby_days_limit: nb_stock_useby_days_limit || null,
                  nb_stock_useby_starts_at: nb_stock_useby_starts_at || null,
                  nb_stock_useby_ends_at: nb_stock_useby_ends_at || null,
                })
              }
              timeZoneName={companyTimeZoneName}
              readOnly={readOnlyAccess}
            />
          </div>
        )}

        {product_type !== "accommodation" && product_booking_type !== "static_date_booking" && (
          <div>
            <FormControl.NumberLimitFormGroup
              {...FormControl.addProps(`${name}.default_total_stock_limit`, {
                errors,
              })}
              value={product_variant.nb_total_stock_limit}
              onBlur={onBlur}
              onChange={this.handleProductVariantPropChange.bind(this, "nb_total_stock_limit")}
              label={I18n.humanAttributeName("product_schedule_slot", "total_stock_limit")}
              min={1}
              readOnly={readOnlyAccess}
              daysLimitDisabled={true}
              additionalInfo={
                <NoBookingStockAvailability variant={product_variant} display={product_variant.nb_total_stock_limit} />
              }
            />
          </div>
        )}

        {product_booking_type === "no_booking" && (
          <div className="vertically-padded">
            <FormControl.SelectFormGroup
              {...FormControl.addProps(`${name}.nb_requires_company_approval`, {
                errors,
              })}
              value={product_variant.nb_requires_company_approval}
              onBlur={onBlur}
              onChange={this.handleProductVariantPropChange.bind(this, "nb_requires_company_approval")}
              label={I18n.t("papp.accommodation.AccommodationSchedulerView.availabilityTypeHeader")}
              options={[
                [
                  true,
                  I18n.t(
                    "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_company_approval"
                  ),
                ],
                [
                  false,
                  I18n.t(
                    "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_instant_booking"
                  ),
                ],
              ]}
              className="u-massiveWidth"
              help={
                !product_variant.nb_requires_company_approval
                  ? I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking_help")
                  : I18n.humanAttributeName("product_schedule_slot", "available_with_company_approval_help")
              }
            />
          </div>
        )}

        {["static_date_booking", "static_date_range_booking"].includes(product_booking_type) && (
          <div>
            <FormControl.NumberLimitFormGroup
              {...FormControl.addProps(`${name}.default_total_stock_limit`, {
                errors,
              })}
              value={product_variant.default_total_stock_limit}
              onBlur={onBlur}
              onChange={this.handleProductVariantPropChange.bind(this, "default_total_stock_limit")}
              label={I18n.humanAttributeName("product_schedule_slot", "total_stock_limit")}
              min={1}
              readOnly={readOnlyAccess}
            />

            <FormControl.SelectFormGroup
              {...FormControl.addProps(`${name}.default_requires_company_approval`, { errors })}
              value={product_variant.default_requires_company_approval}
              onBlur={onBlur}
              onChange={this.handleProductVariantPropChange.bind(this, "default_requires_company_approval")}
              label={I18n.t("papp.accommodation.AccommodationSchedulerView.availabilityTypeHeader")}
              options={[
                [
                  true,
                  I18n.t(
                    "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_company_approval"
                  ),
                ],
                [
                  false,
                  I18n.t(
                    "papp.accommodation.AccommodationSchedulerView.availabilityTypes.available_with_instant_booking"
                  ),
                ],
              ]}
              className="u-massiveWidth"
              help={
                !product_variant.default_requires_company_approval
                  ? I18n.humanAttributeName("product_schedule_slot", "available_with_instant_booking_help")
                  : I18n.humanAttributeName("product_schedule_slot", "available_with_company_approval_help")
              }
            />

            {this.renderDefaultSaleWindowInputs()}
          </div>
        )}
      </div>
    );
  }
}

@connect(
  (state) => ({
    companyTimeZoneName: state.auth.company.model.time_zone_name,
    initialProduct: stateSelector(state).initialProduct,
    product: stateSelector(state).product,
    submitLoading: stateSelector(state).submitLoading,
    valid: stateSelector(state).valid,
    errors: stateSelector(state).errors,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        updateProduct,
        validate,
        save,
      },
      dispatch
    )
)
export default class StepBookingSettingsForm extends React.PureComponent {
  handleSubmit(event) {
    const { save } = this.props;
    event.preventDefault();
    save();
  }

  handleProductPropChange(prop, value) {
    const { product, updateProduct } = this.props;
    updateProduct({
      ...product,
      [prop]: value,
    });
  }

  handleProductPropsChange(props) {
    const { product, updateProduct } = this.props;
    updateProduct({
      ...product,
      ...props,
    });
  }

  handleProductVariantChange(nextProductVariant) {
    const { product, updateProduct } = this.props;
    //debugger
    updateProduct({
      ...product,
      product_variants: product.product_variants.map((x) =>
        x.uuid === nextProductVariant.uuid ? nextProductVariant : x
      ),
    });
  }

  renderProductVariantFormSection(product_variant) {
    const { readOnlyAccess, companyTimeZoneName, product, errors, validate } = this.props;
    const onBlur = validate;
    return (
      <div>
        <ProductVariantFormSection
          readOnlyAccess={readOnlyAccess}
          name={`product.product_variants[${product_variant.uuid}]`}
          errors={errors}
          product_variant={product_variant}
          product_booking_type={product.booking_type}
          product_type={product.product_type}
          onBlur={onBlur}
          onChange={this.handleProductVariantChange.bind(this)}
          companyTimeZoneName={companyTimeZoneName}
        />
      </div>
    );
  }

  _isProductVariantValid(pv) {
    const { errors } = this.props;
    // return errors.hasValidProp(`product.product_variants[${pv.uuid}].pricelist_type`)
    //   && errors.hasValidProp(`product.product_variants[${pv.uuid}].product_pricelist_items`)
    return true;
  }

  render() {
    const { readOnlyAccess, initialProduct, product, submitLoading, valid, errors, validate } = this.props;
    const onBlur = validate;

    const bookingRemovalWarning = initialProduct.booking_type === "static_date_booking" &&
      product.booking_type === "no_booking" && (
        <span className="color-negative">
          <strong>{I18n.t("papp.common.attention")}:</strong>{" "}
          {I18n.t("papp.products.EditProductView.StepBookingSettings.slots_will_be_removed_warning")}
        </span>
      );

    const couponCodeRealisationPlaceTypeOptions = [
      [
        "online_website",
        I18n.t("papp.products.EditProductView.StepPricelist.coupon_codes_realisation_place_online_website"),
      ],
      ["manually", I18n.t("papp.products.EditProductView.StepPricelist.coupon_codes_realisation_place_manually")],
      ["custom", I18n.t("papp.products.EditProductView.StepPricelist.coupon_codes_realisation_place_custom")],
    ];

    const { companyTimeZoneName } = this.props;

    return (
      <HintView.Container>
        <HintView.Content>
          <Form className="text" onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={submitLoading}>
            <h1>{I18n.t("papp.products.EditProductView.StepBookingSettings.form_title")}</h1>

            {!!errors.base && <FormMessage error={errors.base} />}

            {product.product_type === "standard" && (
              <FormControl.CheckboxFormGroup
                {...FormControl.addProps(`product.booking_type`, { errors })}
                checked={product.booking_type === "static_date_booking"}
                onBlur={onBlur}
                onChange={(checked) =>
                  this.handleProductPropChange("booking_type", checked ? "static_date_booking" : "no_booking")
                }
                label={I18n.t("papp.products.EditProductView.StepBookingSettings.static_date_booking_label")}
                help={bookingRemovalWarning}
                toggle
                readOnly={readOnlyAccess}
              />
            )}

            <FormControl.CheckboxFormGroup
              {...FormControl.addProps(`product.qr_redemption_enabled`, { errors })}
              checked={!!product.qr_redemption_enabled}
              onBlur={onBlur}
              onChange={(checked) => this.handleProductPropChange("qr_redemption_enabled", checked)}
              label={I18n.t("papp.products.EditProductView.StepBookingSettings.qr_redemption_enabled_label")}
              help={I18n.t("papp.products.EditProductView.StepBookingSettings.qr_redemption_enabled_help")}
              toggle
              readOnly={readOnlyAccess}
            />

            {product.booking_type === "no_booking" && (
              <div className="u-marginBottom">
                <h3>{I18n.t("papp.products.EditProductView.StepBookingSettings.global_settings")}</h3>
              </div>
            )}
            {product.booking_type === "static_date_booking" && (
              <div className="u-marginBottom">
                <h3>{I18n.t("papp.products.EditProductView.StepBookingSettings.default_settings")}</h3>
              </div>
            )}

            <FormControl.UsebyDateRangeFormGroup
              label={I18n.t("papp.products.EditProductView.StepBookingSettings.availability_limit_label")}
              value={{
                days_limit: product.available_days_limit,
                starts_at: product.available_from,
                ends_at: product.available_to,
              }}
              onBlur={onBlur}
              onChange={({ days_limit: available_days_limit, starts_at: available_from, ends_at: available_to }) =>
                this.handleProductPropsChange({
                  available_days_limit: available_days_limit || null,
                  available_from: available_from || null,
                  available_to: available_to || null,
                })
              }
              timeZoneName={companyTimeZoneName}
              readOnly={readOnlyAccess}
              daysLimitDisabled={true}
            />

            <ProductVariantsListing
              product={product}
              isProductVariantValid={(pv) => this._isProductVariantValid(pv)}
              renderProductVariant={(pv) => this.renderProductVariantFormSection(pv)}
            />

            <br />

            {product.product_type === "coupon" && (
              <div className="u-marginTop">
                <h3>{I18n.t("papp.products.EditProductView.StepPricelist.coupon_codes_title")}</h3>
                <p>{I18n.t("papp.products.EditProductView.StepPricelist.coupon_codes_description")}</p>
                <ProductVariantsListing
                  product={product}
                  isProductVariantValid={(pv) =>
                    errors.hasValidProp(`product.product_variants[${pv.uuid}].coupon_codes`)
                  }
                  renderProductVariant={(pv) => (
                    <ProductVariantCouponCodesFormSection
                      readOnlyAccess={readOnlyAccess}
                      name={`product.product_variants[${pv.uuid}].coupon_codes`}
                      errors={errors}
                      product={product}
                      product_variant={pv}
                      onBlur={onBlur}
                      onChange={this.handleProductVariantChange.bind(this)}
                    />
                  )}
                />

                {
                  /* temporarily disabled */ false && (
                    <div className="u-marginTop">
                      <FormControl.MultipleRadioFormGroup
                        {...FormControl.addProps(`product.coupon_code_realisation_place_type`, { errors })}
                        value={product.coupon_code_realisation_place_type}
                        onBlur={onBlur}
                        onChange={this.handleProductPropChange.bind(this, "coupon_code_realisation_place_type")}
                        label={I18n.t(
                          "papp.products.EditProductView.StepPricelist.coupon_code_realisation_place_type_label"
                        )}
                        inlineLabel
                        readOnly={readOnlyAccess}
                        options={couponCodeRealisationPlaceTypeOptions}
                      />

                      {product.coupon_code_realisation_place_type === "custom" && (
                        <FormControl.TextAreaFormGroup
                          {...FormControl.addProps(`product.coupon_code_realisation_place_custom_description`, {
                            errors,
                          })}
                          value={product.coupon_code_realisation_place_custom_description}
                          onBlur={onBlur}
                          onChange={this.handleProductPropChange.bind(
                            this,
                            "coupon_code_realisation_place_custom_description"
                          )}
                          placeholder={I18n.t(
                            "papp.products.EditProductView.StepPricelist.coupon_code_realisation_place_custom_description_placeholder"
                          )}
                          readOnly={readOnlyAccess}
                          required
                          autoHeight
                        />
                      )}
                    </div>
                  )
                }
              </div>
            )}

            <button type="submit" disabled={submitLoading} hidden />
          </Form>
        </HintView.Content>
        <HintView.Sidebar>
          <Hint />
        </HintView.Sidebar>
      </HintView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/StepBookingSettings/StepBookingSettingsForm.jsx